import { DatePeriod } from '@kurtosys/types/common/DatePeriod.js';
import { IDateDuration } from '@kurtosys/types/common/IDateDuration.js';
import { TTranslateFn } from '@kurtosys/types/common/TTranslateFn.js';
import { IDateSymbol } from '@kurtosys/types/data/IDateSymbol.js';
import { IFormatOptions } from '@kurtosys/types/data/IFormatOptions.js';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
dayjs.extend(isSameOrAfter);
dayjs.extend(advancedFormat);

import { months3char, monthsFull, weekdays2char, weekdays3char, weekdaysFull } from './dateOptions.js';
import { isDate } from './typeChecks.js';

export function getDateDifference(endDate: Date, startDate: Date, period: DatePeriod): number {
	let dateDifference = 0;

	if (period === 'SECONDS') {
		dateDifference = dayjs(endDate).diff(startDate, 'second');
	}

	if (period === 'MINUTES') {
		dateDifference = dayjs(endDate).diff(startDate, 'minute');
	}

	if (period === 'HOURS') {
		dateDifference = dayjs(endDate).diff(startDate, 'hour');
	}

	if (period === 'DAYS') {
		dateDifference = dayjs(endDate).diff(startDate, 'day');
	}

	if (period === 'WEEKS') {
		dateDifference = dayjs(endDate).diff(startDate, 'week');
	}

	if (period === 'MONTHS') {
		dateDifference = dayjs(endDate).diff(startDate, 'month');
	}

	if (period === 'YEARS') {
		dateDifference = dayjs(endDate).diff(startDate, 'year');
	}

	// date-fns differenceIn* functions may return a negative value if startDate is before endDate
	// The difference is still valid as long as we ensure a positive value
	return dateDifference < 0 ? Math.abs(dateDifference) : dateDifference;
}

// Will check if the date is not past a certain duration
// For instance if a document is valid for 6 months, check the document's date against the current date to see if 6 months has passed
export function hasDateExpired(startDate: Date, duration: IDateDuration, now?: Date): boolean {
	if (!now) {
		now = new Date();
	}
	const dateDifference = getDateDifference(now, startDate, duration.period);
	return dateDifference >= duration.value;
}

// Will check if a future date has passed the current date
// For instance if a user has agreed to be remembered for 3 days
export function hasDatePassed(date: Date, now?: Date): boolean {
	if (!now) {
		now = new Date();
	}
	const dateHasPassed = dayjs(now).isSameOrAfter(date);
	return dateHasPassed;
}

/**
 * Validate whether the parameter is a Date object and is not "Invalid Date"
 *
 * @export
 * @param date
 * @returns
 */
export function isValidDate(date: any): boolean {
	return isDate(date) && !isNaN(date.getTime());
}

/**
 * Creates a new Date object using the date string (YYYY-MM-DD HH:mm:SS) provided within the UTC time zone
 *
 * @export
 * @param [dateStr]
 * @returns
 */
export function getDateAsUTC(dateStr?: string): Date {
	const userDate = new Date(dateStr);
	const year = userDate.getFullYear().toString().padStart(4, '0');
	const month = (userDate.getMonth() + 1).toString().padStart(2, '0');
	const day = userDate.getDate().toString().padStart(2, '0');
	const hours = userDate.getHours().toString().padStart(2, '0');
	const minutes = userDate.getMinutes().toString().padStart(2, '0');
	const seconds = userDate.getSeconds().toString().padStart(2, '0');
	const utcDateStr = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
	return new Date(utcDateStr);
}

/**
 * Create a new Date object using the date string (YYYY-MM-DD HH:mm:SS) provided within the User's time zone, converting it to UTC.
 *
 * @export
 * @param [dateStr]
 * @returns
 */
export function getUTCDate(dateStr?: string): Date {
	const userDate = new Date(dateStr);
	const year = userDate.getUTCFullYear().toString().padStart(4, '0');
	const month = (userDate.getUTCMonth() + 1).toString().padStart(2, '0');
	const day = userDate.getUTCDate().toString().padStart(2, '0');
	const hours = userDate.getUTCHours().toString().padStart(2, '0');
	const minutes = userDate.getUTCMinutes().toString().padStart(2, '0');
	const seconds = userDate.getUTCSeconds().toString().padStart(2, '0');
	const utcDateStr = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
	return new Date(utcDateStr);
}

export function getUTCDateString(date?: Date): string {
	if (!date) {
		date = new Date();
	}
	const year = date.getUTCFullYear();
	const month = date.getUTCMonth() + 1;
	const day = date.getUTCDate();
	return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day
		.toString()
		.padStart(2, '0')}`;
}

export function getUTCFullYear(date?: Date): number {
	if (!date) {
		date = new Date();
	}
	return date.getUTCFullYear();
}

export function formatDateWithStringFormat(
	date: Date | string | number,
	format: string,
	translate?: TTranslateFn,
): string {
	const locale = buildFormatLocale(translate) as Record<string, unknown>;
	dayjs.locale('kurtosys', locale);

	return dayjs(date).format(format);
}
export function buildFormatLocale(translate?: TTranslateFn): object {
	// Note: in English, the names of days of the week and months are capitalized.
	// If you are making a new locale based on this one, check if the same is true for the language you're working on.
	// Generally, formatted dates should look like they are in the middle of a sentence,
	// e.g. in Spanish language the weekdays and months should be in the lowercase.

	// For more on what can be customized please see https://day.js.org/docs/en/customization/customization
	const localeObject = {
		name: 'kurtosys',
		weekdays: weekdaysFull.map((day) => (translate ? translate(day) : day)),
		weekdaysShort: weekdays3char.map((day) => (translate ? translate(day) : day)),
		weekdaysMin: weekdays2char.map((day) => (translate ? translate(day) : day)),
		months: monthsFull.map((month) => (translate ? translate(month) : month)),
		monthsShort: months3char.map((month) => (translate ? translate(month) : month)),
		ordinal: (number) => {
			const ordinalSuffixes = ['th', 'st', 'nd', 'rd'];
			const remainder = number % 100;
			const ordinalSuffix =
				ordinalSuffixes[(remainder - 20) % 10] || ordinalSuffixes[remainder] || ordinalSuffixes[0];
			return `[${number}${translate ? translate(ordinalSuffix) : ordinalSuffix}]`;
		},
	};

	return localeObject;
}

// function ordinal(number) {
// 	const rem100 = number % 100;
// 	if (rem100 > 20 || rem100 < 10) {
// 		switch (rem100 % 10) {
// 			case 1:
// 				return number + 'st';
// 			case 2:
// 				return number + 'nd';
// 			case 3:
// 				return number + 'rd';
// 		}
// 	}
// 	return number + 'th';
// }

const getFormatSymbolData = (format: string, symbolChar: string): IDateSymbol => {
	const formatCharacters = format.split('');
	let start;
	let length = 0;

	for (let i = 0; i < formatCharacters.length; i++) {
		const char = formatCharacters[i];
		const prevChar = i > 0 ? formatCharacters[i - 1] : null;
		if (start === undefined) {
			if (char === symbolChar) {
				start = i;
				length++;
			}
		} else if (prevChar === symbolChar && char === symbolChar) {
			length++;
		}
	}

	return {
		start,
		length,
		key: format.slice(start, start + length),
	};
};

const getDatePart = (date: string, symbol: IDateSymbol, key: string, defaultPart?: string): string => {
	if (date && symbol && symbol.length > 0 && symbol.key === key) {
		return date.slice(symbol.start, symbol.start + symbol.length);
	}
	return defaultPart;
};

const formatGmtOffset = (hours: number): string => {
	if (hours === 0) {
		return 'GMT';
	}
	return `GMT${hours > 0 ? `+${hours}` : hours}`;
};

/**
 * This will take the defined timeZone or fallback to the user's timeZone to provide the GMT offset
 *
 * @param [targetDateStr] string
 * @param [targetTimeZone] string (defaults to user's timezone)
 * @returns string eg. GMT+2
 */
const getGmtOffset = (targetDateStr: string, targetTimeZone?: string): string => {
	// If we are targeting UTC, the offset will simply be GMT.
	if (targetTimeZone === 'UTC') {
		return 'GMT';
	}
	// If we are targeting another timezone, get the difference between UTC and the target time zone
	if (targetTimeZone) {
		const utcDate = getDateAsUTC(targetDateStr);
		if (!isValidDate(utcDate)) {
			return '';
		}
		// here we get timestamps from new date objects based on locale strings using the en-US culture which the Date constructor can read back.
		const utcTime = new Date(utcDate.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
		const tzTime = new Date(utcDate.toLocaleString('en-US', { timeZone: targetTimeZone })).getTime();
		// determine the hour offset between the two time zones.
		const diff = ((utcTime - tzTime) / 3600000) * -1;
		return formatGmtOffset(diff);
	}
	// Otherwise we are targeting the user's time zone to workout the time zone difference
	if (!targetTimeZone) {
		// Keeping DST in mind we create a date based on the date string
		const targetDate = new Date(targetDateStr);
		if (!isValidDate(targetDate)) {
			return '';
		}
		const diff = (targetDate.getTimezoneOffset() / 60) * -1;
		return formatGmtOffset(diff);
	}
};

interface IDateFromFormatProps {
	/**
	 * The date can be a date
	 */
	date: string | number;
	fromFormat: string;
	intlOptions: IFormatOptions;
}
const dateFromFormat = (props: IDateFromFormatProps): Date => {
	const { date, fromFormat, intlOptions } = props;
	let newDate: string | number = date;
	if (date && typeof date === 'string') {
		// determine what date parts can be pulled off of the provided date string.
		const usedSymbols = {
			year: getFormatSymbolData(fromFormat, 'Y'),
			month: getFormatSymbolData(fromFormat, 'M'),
			day: getFormatSymbolData(fromFormat, 'D'),
			hour: getFormatSymbolData(fromFormat, 'H'),
			minute: getFormatSymbolData(fromFormat, 'm'),
			second: getFormatSymbolData(fromFormat, 'S'),
		};

		// Get date part values based on from format.
		const dateTimeParts = {
			year: getDatePart(date, usedSymbols.year, 'YYYY'),
			month: getDatePart(date, usedSymbols.month, 'MM'),
			day: getDatePart(date, usedSymbols.day, 'DD'),
			hour: getDatePart(date, usedSymbols.hour, 'HH', '00'),
			minute: getDatePart(date, usedSymbols.minute, 'mm', '00'),
			second: getDatePart(date, usedSymbols.second, 'SS', '00'),
		};

		// build date & time strings
		const datePart = `${dateTimeParts.year}/${dateTimeParts.month}/${dateTimeParts.day}`;
		const timePart = `${dateTimeParts.hour}:${dateTimeParts.minute}:${dateTimeParts.second}`;
		const dateStrWithNoTimeZone = `${datePart} ${timePart}`;

		// determine time zone to use in order to provide GMT offset as a string.
		let appliedTimeZone;
		if (!intlOptions.dateFormatString && intlOptions.timeZone) {
			appliedTimeZone = intlOptions.timeZone;
		}
		const zonePart = `${getGmtOffset(dateStrWithNoTimeZone, appliedTimeZone)}`;

		// return date string that lines up with the target timezone so that date formatting does not shift out by a day.
		newDate = `${dateStrWithNoTimeZone} ${zonePart}`;
	}

	return new Date(newDate);
};
